import { CheckCircle2, CircleAlert, Clock3, RotateCw } from 'lucide-react';
import { Pill } from '@/shared/components';
import type { AvailabilityDistributionOperation } from '@/shared/api';
import { ariOperationLabel } from '@/features/properties/ari-edit';
import { ariErrorSentence, ariStatusSentence } from './ariOperationText';

interface AvailabilityDistributionOperationListProps {
  operations: AvailabilityDistributionOperation[];
}

export function AvailabilityDistributionOperationList({
  operations,
}: AvailabilityDistributionOperationListProps) {
  if (operations.length === 0) return null;
  return (
    <ol className="divide-hairline mt-2 divide-y">
      {operations.map((operation) => (
        <li key={operation.id} className="flex items-start gap-2 py-2">
          <StatusIcon status={operation.status} />
          <div className="min-w-0">
            <Pill variant={tone(operation.status)} size="sm">
              {ariOperationLabel(operation.status)}
            </Pill>
            <p className="text-ink-soft mt-1 text-xs">
              {ariStatusSentence(operation)}
              {ariErrorSentence(operation) ? ` ${ariErrorSentence(operation)}` : ''}
              {operation.attempts > 1 ? ` · ${operation.attempts} Versuche` : ''}
              {/* Der Rohcode ist für den Support; klein, nie statt des Satzes. */}
              {operation.errorCode && (
                <span className="text-ink-faint ml-1 font-mono text-[10px]">
                  {operation.errorCode}
                </span>
              )}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function StatusIcon({ status }: { status: AvailabilityDistributionOperation['status'] }) {
  if (status === 'Confirmed' || status === 'SentUnverified')
    return <CheckCircle2 className="text-forest mt-0.5 shrink-0" size={14} />;
  if (status === 'Queued') return <Clock3 className="text-ink-faint mt-0.5 shrink-0" size={14} />;
  if (status === 'Retrying')
    return <RotateCw className="text-amber-ink mt-0.5 shrink-0" size={14} />;
  return <CircleAlert className="text-sealwax mt-0.5 shrink-0" size={14} />;
}

function tone(
  status: AvailabilityDistributionOperation['status'],
): 'live' | 'warning' | 'stop' | 'default' {
  if (status === 'Confirmed' || status === 'SentUnverified') return 'live';
  if (status === 'Queued') return 'default';
  if (status === 'Failed') return 'stop';
  return 'warning';
}
